import { useCallback, useMemo, useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Reorder } from 'framer-motion'
import { ResultConceptItemTableRowFragment } from 'generated/resources/graphql'
import { isNil } from 'lodash'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { PayrollResultColumnConfig } from 'modules/payroll_bundle/hooks/useEmployeeResultsSummaries'
import { usePayrollResults } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'

import Field from './Field'
import './index.styles.css'
interface Props {
  open: boolean
  onClose: () => void
  mappedResultConcepts: ResultConceptItemTableRowFragment['concept'][]
}

const EditColumnSidepanel = ({ open, onClose, mappedResultConcepts }: Props) => {
  const { resultDispatch } = usePayrollResults()
  const fetchConfig = (): PayrollResultColumnConfig[] => {
    const localStorageItem = localStorage.getItem(
      LOCAL_STORAGE_KEYS.payrollResultsTableColumnConfig
    )
    return localStorageItem ? JSON.parse(localStorageItem) : []
  }
  const [resultColumnConfigs, setResultColumnConfigs] = useState<PayrollResultColumnConfig[]>([])

  const isActiveColumn = (configId: number) => {
    const columnPresent = resultColumnConfigs.find((config) => config.id === configId)
    return columnPresent ? columnPresent.isVisible : false
  }

  // We use this value to know when we have to update the local storage keys
  const defaultConfigKeys = fetchConfig()
    .map((config) => config.name)
    .sort((a, b) => a.localeCompare(b))
    .join('-')

  useUnsafeEffect(() => {
    /* Hack Alert: At this point there is no way to tell that LOCAL_STORAGE_KEYS
     * was modified up in the tree. Therefore this effect was underreacting.
     * So, I'm doing a hack of comparing the local storage with the current state
     *
     * A more proper way would be to pass the payrollResultsTableColumnConfig
     * from the same place we down this data. Either trough prop drilling or
     * through context, but I'm lacking context for that fix
     */
    const config = fetchConfig()

    const configKeys = config.map((concept) => concept.id).join('-')
    const currentConfigKeys = resultColumnConfigs.map((concept) => concept.id).join('-')
    if (configKeys !== currentConfigKeys) {
      setResultColumnConfigs(config)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultConfigKeys])

  const topSectionFixedColumns = useMemo(
    () => [
      {
        id: 1,
        label: i18n.t('payroll_bundle.results_payments.results.list.header.employee'),
        default: false,
      },
      {
        id: 2,
        label: i18n.t('payroll_bundle.results_payments.results.list.header.status'),
        default: false,
      },
    ],
    []
  )

  const onSave = useCallback(() => {
    const newColumnsToSave =
      resultColumnConfigs.length === 0
        ? []
        : resultColumnConfigs
            .filter((config) => !config || config.isVisible)
            .map((config) => mappedResultConcepts.find((concept) => concept.id === config.id))

    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        sortedResultColumns: newColumnsToSave.filter((config) => !isNil(config)),
      },
    })

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.payrollResultsTableColumnConfig,
      JSON.stringify(resultColumnConfigs)
    )

    onClose()
  }, [resultColumnConfigs, mappedResultConcepts, resultDispatch, onClose])

  const onToggle = (configId: number, value: boolean) => {
    const newConfig = resultColumnConfigs.map((config) =>
      config.id === configId ? { ...config, isVisible: value } : config
    )

    const filteredConfig = newConfig.filter(
      (config): config is PayrollResultColumnConfig => config !== undefined
    )

    setResultColumnConfigs(filteredConfig)
  }

  return (
    <Sidepanel
      overlay={false}
      boxPadding
      width="narrow"
      title={i18n.t('payroll_bundle.results_payments.results.summary.edit_columns')}
      onClose={onClose}
      open={open}
      headerBorder
      closeLabel={i18n.t('common.close')}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('payroll_bundle.results_payments.results.summary.cancel')}
          </Button>
          <Button onClick={onSave}>
            {i18n.t('payroll_bundle.results_payments.results.summary.save_changes')}
          </Button>
        </>
      }
    >
      <Box flexDirection="column" gap="s16">
        <Text.Normal weight="semibold" color="grey1000">
          {i18n.t('payroll_bundle.results_payments.results.edit_columns.fixed_column')}
        </Text.Normal>
        <Box className={'EditColumnSidepanel_list'}>
          {topSectionFixedColumns.map((column) => (
            <Box className={'EditColumnSidepanel_item'} key={column.id}>
              <Field label={column.label} sortable={false} active />
            </Box>
          ))}
        </Box>
        <Text.Normal weight="semibold" color="grey1000">
          {i18n.t('payroll_bundle.results_payments.results.edit_columns.editable_column')}
        </Text.Normal>
        <Reorder.Group
          axis="y"
          values={resultColumnConfigs}
          onReorder={setResultColumnConfigs}
          className={'EditColumnSidepanel_list'}
        >
          {resultColumnConfigs.map((config) => (
            <Reorder.Item
              key={config.id}
              value={config}
              className={`${'EditColumnSidepanel_item'} ${'EditColumnSidepanel_draggableItem'}`}
            >
              <Field
                label={config.label || config.name}
                onToggle={(value) => onToggle(config.id, value)}
                active={isActiveColumn(config.id)}
                sortable
              />
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </Box>
    </Sidepanel>
  )
}

export default observer(EditColumnSidepanel)
